import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, ArrowLeft, Mail, Lock, User, ShieldCheck, Check, X, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters', pass: password.length >= 8 },
    { label: 'Uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', pass: /[a-z]/.test(password) },
    { label: 'Number', pass: /[0-9]/.test(password) },
  ];
  const strength = checks.filter(c => c.pass).length;
  const colors = ['#EF4444', '#F59E0B', '#F59E0B', '#10B981', '#10B981'];

  if (!password) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="password-strength">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{ background: i < strength ? colors[strength] : 'rgba(255,255,255,0.1)' }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: c.pass ? '#10B981' : '#475569' }}>
            {c.pass ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  const isPasswordStrong = () => {
    return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
  };

  const handleSendOTP = async () => {
    if (!email.trim()) { toast.error('Please enter your email first'); return; }
    if (!name.trim()) { toast.error('Please enter your name first'); return; }
    if (!isPasswordStrong()) { toast.error('Please create a stronger password'); return; }

    setOtpLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { email, name });
      setOtpSent(true);
      setOtpCountdown(60);
      toast.success('Verification code sent! Check your email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (mode === 'register') {
        if (!name.trim()) { toast.error('Name is required'); setIsLoading(false); return; }
        if (!isPasswordStrong()) { toast.error('Password is too weak'); setIsLoading(false); return; }
        if (!otpCode.trim()) { toast.error('Please enter the verification code'); setIsLoading(false); return; }
        await register(email, password, name, otpCode);
        toast.success('Account verified and created! Welcome to The Regret Engine.');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#050505' }}>
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1661455370615-45b16513c668?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Clarity"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(5,5,5,0.8), rgba(99,102,241,0.1))' }} />
        <div className="relative z-10 p-12 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Syne' }}>
            Rehearse your future before you choose it.
          </h2>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
            AI-powered decision simulation that creates vivid future memories so you can feel the outcome before you live it.
          </p>
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
            <ShieldCheck className="w-5 h-5 shrink-0" style={{ color: '#6366F1' }} />
            <p className="text-xs" style={{ color: '#94A3B8' }}>
              Your data is encrypted and never shared. We verify all accounts to ensure platform integrity.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm mb-8 transition-colors" style={{ color: '#94A3B8' }} data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>

          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }} data-testid="auth-title">
            {mode === 'register' ? 'Create Your Account' : 'Welcome Back'}
          </h1>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            {mode === 'register' ? 'Verify your email to start simulating decisions.' : 'Sign in to continue your simulations.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
                    <Input data-testid="name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                      className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
                  </div>
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
                <Input data-testid="email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                  className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Password</Label>
                {mode === 'login' && (
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs hover:underline" style={{ color: '#6366F1' }}>
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
                <Input data-testid="password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === 'register' ? 'Strong password required' : 'Enter your password'} required
                  className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
              </div>
              {mode === 'register' && <PasswordStrength password={password} />}
            </div>

            {/* OTP Section for Registration */}
            {mode === 'register' && (
              <div className="space-y-3">
                {!otpSent ? (
                  <Button data-testid="send-otp-btn" type="button" onClick={handleSendOTP} disabled={otpLoading || !email || !name || !isPasswordStrong()}
                    variant="outline" className="w-full rounded-lg py-5 text-sm border-white/10 hover:bg-white/5">
                    {otpLoading ? 'Sending...' : <><KeyRound className="w-4 h-4 mr-2" /> Send Verification Code</>}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                      <Check className="w-4 h-4 shrink-0" style={{ color: '#10B981' }} />
                      <p className="text-xs" style={{ color: '#10B981' }}>Verification code sent to {email}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>Verification Code</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
                        <Input data-testid="otp-input" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter 6-digit code" maxLength={6}
                          className="pl-10 py-5 rounded-lg text-sm tracking-[0.3em] text-center font-mono"
                          style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC', fontSize: '18px' }} />
                      </div>
                    </div>
                    <button type="button" onClick={handleSendOTP} disabled={otpCountdown > 0 || otpLoading}
                      className="text-xs hover:underline" style={{ color: otpCountdown > 0 ? '#475569' : '#6366F1' }} data-testid="resend-otp">
                      {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend Code'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <Button data-testid="auth-submit-btn" type="submit" disabled={isLoading || (mode === 'register' && (!otpSent || otpCode.length !== 6))}
              className="w-full rounded-lg py-5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: '#6366F1', boxShadow: '0 0 15px rgba(99,102,241,0.3)' }}>
              {isLoading ? 'Please wait...' : mode === 'register' ? 'Verify & Create Account' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm" style={{ color: '#94A3B8' }}>
            {mode === 'register' ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setOtpSent(false); setOtpCode(''); }}
              className="font-semibold hover:underline" style={{ color: '#6366F1' }}>
              {mode === 'register' ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
